/**
 * Propiedades. Tabla de 7 columnas agrupada por sociedad, con la plantilla de rejilla literal
 * del maquetado.
 *
 * FASE 2: se añade alrededor —filtros, ordenación, conmutador de mapa y alta— SIN tocar el
 * aspecto de la tabla, que es lo único que el maquetado definía y que ya estaba verificado.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PropertyListRow } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Thumbnail } from '../components/Avatar';
import {
  ChevronCell,
  DataTable,
  IdentityCell,
  cellStyles,
} from '../components/DataTable';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonPageHeader } from '../components/Skeleton';
import { formatEur, formatPercent } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { PropertyMap } from '../components/map/LazyPropertyMap';
import {
  EMPTY_FILTERS,
  PropertyFilters,
  type PropertyFilterState,
} from './properties/PropertyFilters';
import { PropertyFormDrawer } from './properties/PropertyFormDrawer';
import { CoverPickerDrawer } from './properties/CoverPickerDrawer';
import { PropertyCard, estado, ocupacion } from './properties/PropertyCard';
import styles from './PropertiesPage.module.css';

/** Plantilla de columnas literal del maquetado. */
const TEMPLATE = '2fr 0.7fr 0.8fr 0.7fr 0.8fr 0.95fr 0.95fr 0.95fr 44px';

/**
 * Tablet: sobreviven inmueble, habitaciones, inquilinos, libres, ocupación y patrimonio. Se
 * caen adquisición y valor de mercado, que son las dos que se consultan de tarde en tarde y
 * las que más ancho piden. Nueve columnas en 800px no se leen: se adivinan.
 */
const TEMPLATE_TABLET = '1.8fr 0.6fr 0.7fr 0.6fr 0.7fr 1fr 36px';

const COLUMNS = [
  { key: 'property', label: 'Propiedad' },
  { key: 'units', label: 'Hab.' },
  /**
   * «Inquilinos» y «Libres» son cosas distintas de «Hab.» y de «Ocup.», y las cuatro caben:
   * los inquilinos son PERSONAS —una habitación con dos titulares cuenta dos— y las libres son
   * las plazas que quedan por alquilar, que es lo que se mira para decidir dónde meter a
   * alguien. Con solo el porcentaje de ocupación hay que hacer la cuenta a mano.
   */
  { key: 'tenants', label: 'Inquil.' },
  { key: 'free', label: 'Libres' },
  { key: 'occ', label: 'Ocup.' },
  { key: 'acq', label: 'Adquisición', desde: 'ancho' as const },
  { key: 'market', label: 'Valor mercado', desde: 'ancho' as const },
  { key: 'equity', label: 'Patrimonio' },
  { key: 'chevron', label: '' },
];

/** Columnas por las que se puede ordenar, y el campo que entiende la API. */
const SORTABLE: Record<string, string> = {
  units: 'occupancy',
  occ: 'occupancy',
  market: 'marketValue',
  equity: 'equity',
};

export function PropertiesPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Qué inmueble tiene abierto el selector de portada. `null` = ninguno. */
  const [cover, setCover] = useState<{ id: string; name: string } | null>(null);

  /**
   * Los filtros SE LEEN DE LA URL. Es lo que hace que un enlace sea compartible, que el botón
   * atrás funcione y que recargar no lo pierda todo.
   */
  const filters: PropertyFilterState = { q: searchParams.get('q') ?? '' };
  /**
   * Tarjetas es la vista por defecto: foto, ocupación y renta de un vistazo, que es como se
   * busca un inmueble. La tabla se queda para comparar cifras columna a columna (y ordenar);
   * el mapa, para verlos por zona.
   */
  const vista = searchParams.get('vista');
  const view = vista === 'mapa' ? 'mapa' : vista === 'tabla' ? 'tabla' : 'tarjetas';
  const sort = searchParams.get('orden') ?? '';
  const dir = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';

  function updateParams(changes: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  function applyFilters(state: PropertyFilterState) {
    // Se limpian también los filtros antiguos (ciudad, estado, valor) de un enlace viejo.
    updateParams({ q: state.q, ciudad: '', estado: '', min: '', max: '' });
  }

  // Solo el buscador: los antiguos filtros de ciudad, estado y valor ya no se aplican.
  const queryParams = {
    ...(filters.q ? { q: filters.q } : {}),
    ...(sort ? { sort, dir } : {}),
  };

  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: queryKeys.properties(queryParams),
    queryFn: () => api.properties(queryParams),
    /**
     * Conserva el resultado anterior mientras llega el nuevo. Sin esto, la tabla parpadea a
     * esqueleto en cada tecla del buscador y el usuario pierde el hilo de lo que estaba viendo.
     */
    placeholderData: (previous) => previous,
  });

  const mapQuery = useQuery({
    queryKey: queryKeys.propertyMap({}),
    queryFn: () => api.propertyMap({}),
    // Solo se pide cuando el mapa está a la vista: es una consulta más y ~250 KB de librería.
    enabled: view === 'mapa',
  });

  const allRows = useMemo(
    () => data?.groups.flatMap((group) => group.properties) ?? [],
    [data],
  );
  const rowsById = useMemo(() => new Map(allRows.map((row) => [row.id, row])), [allRows]);

  const hasFilters = Boolean(filters.q);

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonCard rows={4} height={54} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <PageHeader
          title="Propiedades"
          subtitle="Todos tus inmuebles."
        />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  /*
   * Una sola cartera: los inmuebles van en una lista, sin cabeceras de grupo. Se aplanan los
   * grupos que devuelve la API (en una base ya migrada solo hay uno).
   */
  const filas = allRows;

  function toggleSort(columnKey: string) {
    const field = SORTABLE[columnKey];
    if (!field) return;
    if (sort === field) {
      updateParams({ orden: field, dir: dir === 'desc' ? 'asc' : 'desc' });
    } else {
      updateParams({ orden: field, dir: 'desc' });
    }
  }

  const columns = COLUMNS.map((column) => {
    const field = SORTABLE[column.key];
    if (!field || !column.label) return column;
    const activeSort = sort === field;
    return {
      ...column,
      label: (
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => toggleSort(column.key)}
          aria-label={`Ordenar por ${column.label}`}
        >
          {column.label}
          <span className={activeSort ? styles.sortActive : styles.sortIdle} aria-hidden="true">
            {activeSort ? (dir === 'desc' ? '▾' : '▴') : '⇅'}
          </span>
        </button>
      ) as unknown as string,
    };
  });

  return (
    <section>
      <PageHeader
        title="Propiedades"
        subtitle="Todos tus inmuebles."
        actions={
          <div className={styles.headerActions}>
            <div className={styles.viewToggle} role="group" aria-label="Vista">
              <button
                type="button"
                className={view === 'tarjetas' ? styles.viewActive : styles.viewButton}
                onClick={() => updateParams({ vista: '' })}
                aria-pressed={view === 'tarjetas'}
              >
                Tarjetas
              </button>
              <button
                type="button"
                className={view === 'tabla' ? styles.viewActive : styles.viewButton}
                onClick={() => updateParams({ vista: 'tabla' })}
                aria-pressed={view === 'tabla'}
              >
                Tabla
              </button>
              <button
                type="button"
                className={view === 'mapa' ? styles.viewActive : styles.viewButton}
                onClick={() => updateParams({ vista: 'mapa' })}
                aria-pressed={view === 'mapa'}
              >
                Mapa
              </button>
            </div>

            {can('property:create') && (
              <Button variant="primary" onClick={() => setDrawerOpen(true)} data-tour="alta-inmueble">
                <Icon name="plus" size={17} strokeWidth={2.4} />
                Añadir inmueble
              </Button>
            )}
          </div>
        }
      />

      <PropertyFilters
        value={filters}
        onChange={applyFilters}
        resultCount={allRows.length}
      />

      {view === 'mapa' ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <PropertyMap
            height={560}
            onNavigate={(href) => navigate(href)}
            markers={(mapQuery.data?.features ?? [])
              // La búsqueda filtra el mapa: solo los inmuebles que también salen en el listado.
              .filter((feature) => !filters.q || rowsById.has(feature.properties.id))
              .map((feature) => {
              // La fila del listado trae lo que el mapa no: portada, renta y rentabilidad.
              const row = rowsById.get(feature.properties.id);
              return {
              id: feature.properties.id,
              latitude: feature.geometry.coordinates[1],
              longitude: feature.geometry.coordinates[0],
              label: feature.properties.name,
              detail: `${feature.properties.city} · ${feature.properties.occupiedUnits}/${feature.properties.units} ocupadas · ${formatEur(feature.properties.marketValue)}`,
              href: `/propiedades/${feature.properties.id}`,
              approximate:
                feature.properties.geocodePrecision === 'municipio' ||
                feature.properties.geocodePrecision === 'sin_geocodificar',
              ...(row
                ? {
                    card: {
                      photoUrl: row.cover?.url ?? null,
                      status: estado(row),
                      place: row.province && row.province !== row.city ? `${row.city} · ${row.province}` : row.city,
                      occupied: row.occupiedUnits,
                      units: row.units,
                      occupancyLabel: ocupacion(row),
                      rent: formatEur(row.monthlyRent),
                      yieldText: formatPercent(row.grossYield, 1),
                    },
                  }
                : {}),
              };
            })}
          />
          {/* Un mapa que oculta inmuebles sin avisar hace pensar que se han perdido. */}
          {(mapQuery.data?.unlocatedCount ?? 0) > 0 && (
            <p className={styles.mapNote}>
              <Icon name="info" size={13} />
              {mapQuery.data!.unlocatedCount} inmueble
              {mapQuery.data!.unlocatedCount === 1 ? '' : 's'} sin ubicar no aparece
              {mapQuery.data!.unlocatedCount === 1 ? '' : 'n'} en el mapa. Edítalos para añadir su
              posición.
            </p>
          )}
        </Card>
      ) : filas.length === 0 ? (
        hasFilters ? (
          /* Estado vacío de BÚSQUEDA, distinto del de «no tienes inmuebles»: lo que no encuentra
             es lo que ha filtrado. */
          <EmptyState
            icon="info"
            title="Ningún inmueble coincide con la búsqueda"
            body="Prueba con otro nombre, calle o ciudad."
            actionLabel="Quitar la búsqueda"
            onAction={() => applyFilters(EMPTY_FILTERS)}
          />
        ) : (
          <EmptyState
            icon="building"
            title="Todavía no hay inmuebles"
            body="Cuando añadas inmuebles aparecerán aquí con su ocupación, valor de mercado y patrimonio."
            {...(can('property:create')
              ? { actionLabel: 'Añadir el primero', onAction: () => setDrawerOpen(true) }
              : {})}
          />
        )
      ) : view === 'tarjetas' ? (
        <div className={isPlaceholderData ? styles.stale : undefined}>
          <div className={styles.cardsGrid}>
            {filas.map((row) => (
              <PropertyCard key={row.id} row={row} />
            ))}
          </div>
        </div>
      ) : (
        <div className={isPlaceholderData ? styles.stale : undefined}>
          {[filas].map((rows) => (
            <Card key="cartera" variant="table" style={{ marginBottom: 18 }}>
              <DataTable<PropertyListRow>
                caption="Propiedades"
                columns={columns}
                template={TEMPLATE}
                templateTablet={TEMPLATE_TABLET}
                tightHeader
                rows={rows}
                rowKey={(row) => row.id}
                onRowClick={(row) => navigate(`/propiedades/${row.id}`)}
                renderRow={(row, visibles) => (
                  <>
                    <IdentityCell
                      visual={
                        <CoverButton row={row} size={46} onPick={() => setCover({ id: row.id, name: row.name })} />
                      }
                      main={row.name}
                      sub={row.city}
                    />
                    <span className={cellStyles.cellRegular}>{row.units}</span>
                    <span className={cellStyles.cellRegular}>{row.tenantCount}</span>
                    {/* Sin plazas libres no hay nada que alquilar: se atenúa en vez de gritar. */}
                    <span
                      className={row.freeUnits > 0 ? cellStyles.cellStrong : cellStyles.cellMuted}
                    >
                      {row.freeUnits}
                    </span>
                    <span className={cellStyles.cellSuccess}>
                      {formatPercent(row.occupancyRate, 0)}
                    </span>
                    {visibles.has('acq') && (
                      <span className={cellStyles.cellRegular}>
                        {formatEur(row.acquisitionPrice)}
                      </span>
                    )}
                    {visibles.has('market') && (
                      <span className={cellStyles.cellRegular}>{formatEur(row.marketValue)}</span>
                    )}
                    <span className={cellStyles.cellStrong}>{formatEur(row.equity)}</span>
                    <ChevronCell />
                  </>
                )}
                /*
                 * Móvil. Tres cifras y no nueve: cómo va de lleno, cuántas plazas quedan por
                 * alquilar y cuánta gente vive ahí. Es lo que se mira para decidir dónde meter
                 * a alguien; el valor de mercado y el patrimonio están en la ficha, a un toque.
                 */
                renderCard={(row) => (
                  <>
                    <div className={cellStyles.cardHead}>
                      <IdentityCell
                        visual={
                          <CoverButton row={row} size={44} onPick={() => setCover({ id: row.id, name: row.name })} />
                        }
                        main={row.name}
                        sub={row.city}
                      />
                      <ChevronCell />
                    </div>
                    <div className={cellStyles.cardStats}>
                      <span>
                        <span className={cellStyles.cardStatLabel}>Ocupación</span>
                        <span className={`${cellStyles.cardStatValue} ${cellStyles.cellSuccess}`}>
                          {formatPercent(row.occupancyRate, 0)}
                        </span>
                      </span>
                      <span>
                        <span className={cellStyles.cardStatLabel}>Libres</span>
                        <span
                          className={
                            row.freeUnits > 0
                              ? cellStyles.cardStatValue
                              : `${cellStyles.cardStatValue} ${cellStyles.cellMuted}`
                          }
                        >
                          {row.freeUnits} de {row.units}
                        </span>
                      </span>
                      <span>
                        <span className={cellStyles.cardStatLabel}>Inquilinos</span>
                        <span className={cellStyles.cardStatValue}>{row.tenantCount}</span>
                      </span>
                    </div>
                  </>
                )}
              />
            </Card>
          ))}
        </div>
      )}

      <CoverPickerDrawer
        open={cover !== null}
        onClose={() => setCover(null)}
        propertyId={cover?.id ?? null}
        propertyName={cover?.name ?? ''}
      />

      <PropertyFormDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </section>
  );
}

/**
 * La miniatura de la fila, pulsable para cambiar la portada.
 *
 * ES UN BOTÓN DENTRO DE UNA FILA QUE TAMBIÉN ES PULSABLE, y por eso el `stopPropagation`: sin
 * él, elegir portada abriría además la ficha del inmueble. Es la misma razón por la que no
 * puede ser un enlace: dos destinos en el mismo sitio.
 *
 * Sin fotos no hay nada que elegir, así que ahí no es un botón: es la miniatura vacía de
 * siempre, sin foco ni cursor de mano prometiendo algo que no va a pasar.
 */
function CoverButton({
  row,
  size,
  onPick,
}: {
  row: PropertyListRow;
  size: number;
  onPick: () => void;
}) {
  const thumb = (
    <Thumbnail
      size={size}
      radius={6}
      src={row.cover?.url ?? null}
      placeholderColor={row.cover?.placeholderColor ?? null}
      alt=""
    />
  );

  if (row.photoCount === 0) return thumb;

  return (
    <button
      type="button"
      className={styles.coverButton}
      onClick={(event) => {
        event.stopPropagation();
        onPick();
      }}
      // La fila entera responde a Enter; sin esto, el teclado dispararía las dos cosas.
      onKeyDown={(event) => event.stopPropagation()}
      title="Cambiar la portada"
      aria-label={`Cambiar la portada de ${row.name}. ${row.photoCount} foto${
        row.photoCount === 1 ? '' : 's'
      }.`}
    >
      {thumb}
      <span className={styles.coverOverlay} aria-hidden="true">
        <Icon name="image" size={15} strokeWidth={2} />
      </span>
    </button>
  );
}
